import { Component, computed, effect, inject, input, linkedSignal, untracked } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { FormlyForm } from '@ngx-formly/core';
import { map } from 'lodash-es';
import { controlState, controlValue } from '../../util/form';
import { URI_REGEX } from '../../util/format';

@Component({
  selector: 'app-links',
  templateUrl: './links.component.html',
  styleUrls: ['./links.component.scss'],
  host: { 'class': 'form-group' },
  imports: [ReactiveFormsModule, FormlyForm]
})
export class LinksFormComponent {
  private fb = inject(FormBuilder);

  private readonly rootControlState = controlValue(() => this.group());

  static validators = [Validators.pattern(URI_REGEX)];

  readonly group = input<UntypedFormGroup | undefined>(undefined);
  readonly fieldName = input('links');

  field = {
    type: 'refs',
    props: {
      showLabel: true,
      label: $localize`Sources: `,
      showAdd: true,
      addText: $localize`+ Add another source`,
    },
    fieldArray: {
      focus: false,
      props: {
        label: $localize`🔗️`,
      }
    },
  };

  readonly emoji = input<string | undefined>(undefined);
  readonly label = input<string | undefined>(undefined);
  readonly showLabel = input<boolean | undefined>(undefined);
  readonly add = input<string | undefined>(undefined);
  readonly showAdd = input<boolean | undefined>(undefined);

  constructor() {
    effect(() => {
      const emoji = this.emoji();
      if (emoji !== undefined) this.field.fieldArray.props.label = emoji;
      const label = this.label();
      if (label !== undefined) this.field.props.label = label;
      const showLabel = this.showLabel();
      if (showLabel !== undefined) this.field.props.showLabel = showLabel;
      const add = this.add();
      if (add !== undefined) this.field.props.addText = add;
      const showAdd = this.showAdd();
      if (showAdd !== undefined) this.field.props.showAdd = showAdd;
    });
  }

  readonly links = computed(() => {
    this.rootControlState();
    return this.group()?.get(this.fieldName()) as UntypedFormArray | undefined;
  });

  readonly count = controlState(() => this.links(), c => (c as UntypedFormArray).length);

  /**
   * Formly model, seeded from the links already in the form.
   */
  readonly model = linkedSignal<string[]>(() => {
    const links = this.links();
    return untracked(() => links?.value || []);
  });

  setLinks(values: string[]) {
    this.model.set(values);
    const links = this.links();
    if (!links) return;
    while (links.length > values.length) links.removeAt(links.length - 1, { emitEvent: false });
    while (links.length < values.length) links.push(this.fb.control(''), { emitEvent: false });
    links.setValue(values);
  }

  addLink(...values: string[]) {
    if (!values.length) return;
    this.model.set(this.links()!.value);
    this.field.fieldArray.focus = true;
    for (const value of values) {
      if (value) this.field.fieldArray.focus = false;
      if (value && value !== 'placeholder' && this.model().includes(value)) return;
      this.model.update(model => [...model, value]);
    }
  }

  removeLink(index: number) {
    this.links()?.removeAt(index);
  }
}

export function linksForm(fb: UntypedFormBuilder, urls: string[]) {
  return fb.array(map(urls, v => fb.control(v, LinksFormComponent.validators)));
}
